import { useMemo, useRef, useState, type WheelEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StandaloneDatePicker } from '@/components/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { RichTextEditor } from '@/components/rich-text';
import { Slider } from '@/components/ui/slider';
import { Check, CircleAlert, Plus, Search, Star, Trash2, Upload, X } from 'lucide-react';
import {
  useGetPublicSkillFrameworkQuery,
  useGetSkillFrameworkQuery,
} from '@/state/skills/skills-api';
import {
  type AssignmentItem,
  useCreateAssignmentMutation,
  useCreateCourseSubStructureMutation,
  useGetCourseMarkingSchemeQuery,
  usePatchAssignmentMutation,
} from '@/state/course';
import { skipToken } from '@reduxjs/toolkit/query';
import { format } from 'date-fns';
import { toast } from 'sonner';

type FrameworkSource = 'org' | 'public';

interface SkillTreeNode {
  id: number;
  name: string;
  children: SkillTreeNode[];
}

interface FrameworkSelectionItem {
  id: number;
  key: string;
  source: FrameworkSource;
  name: string;
  children: SkillTreeNode[];
}

interface SkillCandidate {
  id: number;
  key: string;
  frameworkKey: string;
  frameworkName: string;
  name: string;
}

interface SelectedSkillItem extends SkillCandidate {
  level: number;
}

const SKILL_LEVEL_LABELS: Record<number, string> = {
  0: 'Unspecified',
  1: 'Supplementary',
  2: 'Beneficial',
  3: 'Significant',
  4: 'Crucial',
  5: 'Essential',
};

const getSkillLevelLabel = (level: number): string => SKILL_LEVEL_LABELS[level] || 'Unspecified';

const flattenFrameworkSkills = (
  frameworkKey: string,
  frameworkName: string,
  node: SkillTreeNode
): SkillCandidate[] => {
  const current: SkillCandidate = {
    id: node.id,
    key: `${frameworkKey}:${node.id}`,
    frameworkKey,
    frameworkName,
    name: node.name,
  };
  const descendants = (node.children || []).flatMap((child) =>
    flattenFrameworkSkills(frameworkKey, frameworkName, child)
  );
  return [current, ...descendants];
};

interface CourseAssignmentCreateDrawerProps {
  tenant: string;
  courseId: number;
  courseStructureId: number;
  courseSectionId?: number | null;
  assignmentToEdit?: AssignmentItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CourseAssignmentCreateDrawer = ({
  tenant,
  courseId,
  courseStructureId,
  courseSectionId = null,
  assignmentToEdit = null,
  open,
  onOpenChange,
}: CourseAssignmentCreateDrawerProps) => {
  const [title, setTitle] = useState(assignmentToEdit?.assignment_name || '');
  const [details, setDetails] = useState(assignmentToEdit?.assignment_brief || '');
  const [dueDate, setDueDate] = useState(
    assignmentToEdit?.submission_deadline ? assignmentToEdit.submission_deadline.slice(0, 10) : ''
  );
  const [assignmentType, setAssignmentType] = useState(
    assignmentToEdit?.assignment_type === 'GROUP' ? 'Group assignment' : 'Individual assignment'
  );
  const [fullMarks, setFullMarks] = useState(
    String(assignmentToEdit?.marking_scheme?.full_marks ?? 0)
  );
  const [weightage, setWeightage] = useState(
    String(assignmentToEdit?.marking_scheme?.course_percentage ?? 0)
  );
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const availableSkillsScrollRef = useRef<HTMLDivElement | null>(null);
  const selectedSkillsScrollRef = useRef<HTMLDivElement | null>(null);
  const serializedDetailsGetterRef = useRef<(() => string) | null>(null);
  const [createCourseSubStructure, { isLoading: isCreatingSubStructure }] =
    useCreateCourseSubStructureMutation();
  const [createAssignment, { isLoading: isCreatingAssignment }] =
    useCreateAssignmentMutation();
  const [patchAssignment, { isLoading: isPatchingAssignment }] = usePatchAssignmentMutation();

  const [isFrameworkDrawerOpen, setIsFrameworkDrawerOpen] = useState(false);
  const [isAddSkillsDrawerOpen, setIsAddSkillsDrawerOpen] = useState(false);
  const [frameworkTab, setFrameworkTab] = useState<FrameworkSource>('org');
  const [selectedFrameworkKeys, setSelectedFrameworkKeys] = useState<string[]>([]);
  const [skillSearch, setSkillSearch] = useState('');

  const [selectedSkills, setSelectedSkills] = useState<SelectedSkillItem[]>(
    (assignmentToEdit?.assignments_skills || []).map((skill) => ({
      id: skill.skills,
      key: `existing:${skill.skills}`,
      frameworkKey: 'existing',
      frameworkName: 'Existing',
      name: skill.skill_name,
      level: Number(skill.required_level || 0),
    }))
  );
  const [draftSkillLevels, setDraftSkillLevels] = useState<Record<string, number>>({});
  const [draftSelectedSkills, setDraftSelectedSkills] = useState<Record<string, SelectedSkillItem>>({});
  const existingAttachments = useMemo(
    () => assignmentToEdit?.assignment_repo || [],
    [assignmentToEdit]
  );

  const { data: courseMarkingSchemes = [] } = useGetCourseMarkingSchemeQuery(
    tenant && courseId ? { tenant, courseId } : skipToken
  );
  const { data: organizationFrameworksRaw = [] } = useGetSkillFrameworkQuery(
    tenant ? { tenant } : skipToken
  );
  const { data: publicFrameworksRaw = [] } = useGetPublicSkillFrameworkQuery(
    tenant ? { tenant } : skipToken
  );

  const organizationFrameworks = useMemo<FrameworkSelectionItem[]>(
    () =>
      organizationFrameworksRaw.map((f) => ({
        id: Number(f.id),
        key: `org:${f.id}`,
        source: 'org',
        name: f.name,
        children: f.children as SkillTreeNode[],
      })),
    [organizationFrameworksRaw]
  );

  const publicFrameworks = useMemo<FrameworkSelectionItem[]>(
    () =>
      publicFrameworksRaw.map((f) => ({
        id: Number(f.id),
        key: `public:${f.id}`,
        source: 'public',
        name: f.name,
        children: f.children as SkillTreeNode[],
      })),
    [publicFrameworksRaw]
  );

  const allFrameworks = useMemo(
    () => [...organizationFrameworks, ...publicFrameworks],
    [organizationFrameworks, publicFrameworks]
  );

  const selectedFrameworks = useMemo(
    () => allFrameworks.filter((f) => selectedFrameworkKeys.includes(f.key)),
    [allFrameworks, selectedFrameworkKeys]
  );

  const allSkillCandidates = useMemo(() => {
    return selectedFrameworks.flatMap((framework) =>
      flattenFrameworkSkills(framework.key, framework.name, {
        id: framework.id,
        name: framework.name,
        children: framework.children || [],
      })
    );
  }, [selectedFrameworks]);

  const filteredSkillCandidates = useMemo(() => {
    const q = skillSearch.trim().toLowerCase();
    if (!q) return allSkillCandidates;
    return allSkillCandidates.filter((skill) => skill.name.toLowerCase().includes(q));
  }, [allSkillCandidates, skillSearch]);

  const frameworkNamesForSearch = useMemo(
    () => selectedFrameworks.map((f) => f.name),
    [selectedFrameworks]
  );

  const resetForm = () => {
    setTitle(assignmentToEdit?.assignment_name || '');
    setDetails(assignmentToEdit?.assignment_brief || '');
    setDueDate(assignmentToEdit?.submission_deadline ? assignmentToEdit.submission_deadline.slice(0, 10) : '');
    setAssignmentType(
      assignmentToEdit?.assignment_type === 'GROUP' ? 'Group assignment' : 'Individual assignment'
    );
    setFullMarks(String(assignmentToEdit?.marking_scheme?.full_marks ?? 0));
    setWeightage(String(assignmentToEdit?.marking_scheme?.course_percentage ?? 0));
    setAttachments([]);
    setSelectedFrameworkKeys([]);
    setSelectedSkills(
      (assignmentToEdit?.assignments_skills || []).map((skill) => ({
        id: skill.skills,
        key: `existing:${skill.skills}`,
        frameworkKey: 'existing',
        frameworkName: 'Existing',
        name: skill.skill_name,
        level: Number(skill.required_level || 0),
      }))
    );
    setDraftSkillLevels({});
    setDraftSelectedSkills({});
    setSkillSearch('');
    setIsFrameworkDrawerOpen(false);
    setIsAddSkillsDrawerOpen(false);
  };

  const openAddSkillsDrawer = (frameworkKeys: string[]) => {
    const selected = allFrameworks.filter((f) => frameworkKeys.includes(f.key));
    const candidates = selected.flatMap((framework) =>
      flattenFrameworkSkills(framework.key, framework.name, {
        id: framework.id,
        name: framework.name,
        children: framework.children || [],
      })
    );

    setSelectedFrameworkKeys(frameworkKeys);
    setIsFrameworkDrawerOpen(false);
    setSkillSearch('');

    setDraftSkillLevels((prev) => {
      const next = { ...prev };
      candidates.forEach((skill) => {
        if (typeof next[skill.key] !== 'number') next[skill.key] = 0;
      });
      return next;
    });

    setDraftSelectedSkills(() => {
      const seeded = Object.fromEntries(selectedSkills.map((s) => [s.key, s]));
      return seeded;
    });

    setIsAddSkillsDrawerOpen(true);
  };

  const toggleFramework = (frameworkKey: string, checked: boolean) => {
    setSelectedFrameworkKeys((prev) => {
      if (checked) return prev.includes(frameworkKey) ? prev : [...prev, frameworkKey];
      return prev.filter((key) => key !== frameworkKey);
    });
  };

  const activeFrameworkList = frameworkTab === 'org' ? organizationFrameworks : publicFrameworks;
  const parsedDueDate = dueDate ? new Date(dueDate) : undefined;
  const selectedDueDate =
    parsedDueDate && !Number.isNaN(parsedDueDate.getTime()) ? parsedDueDate : undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const assignmentBaseMarkingScheme = useMemo(
    () =>
      courseMarkingSchemes.find(
        (item) =>
          item.course_sub_structure === null &&
          item.course_structure?.assessment_type?.toLowerCase() === 'assignment'
      ),
    [courseMarkingSchemes]
  );
  const totalSubStructureAssignmentWeightage = useMemo(
    () =>
      courseMarkingSchemes
        .filter(
          (item) =>
            item.course_sub_structure !== null &&
            item.course_structure?.assessment_type?.toLowerCase() === 'assignment'
        )
        .reduce((sum, item) => sum + Number(item.course_percentage || 0), 0),
    [courseMarkingSchemes]
  );
  const remainingAssignmentWeightage = useMemo(() => {
    const totalAllowed = Number(assignmentBaseMarkingScheme?.course_percentage || 0);
    const remaining = totalAllowed - totalSubStructureAssignmentWeightage;
    return remaining > 0 ? remaining : 0;
  }, [assignmentBaseMarkingScheme?.course_percentage, totalSubStructureAssignmentWeightage]);

  const handleSkillsPaneWheel = (
    e: WheelEvent<HTMLDivElement>,
    pane: 'available' | 'selected'
  ) => {
    const targetRef = pane === 'available' ? availableSkillsScrollRef : selectedSkillsScrollRef;
    const el = targetRef.current;
    if (!el) return;
    e.preventDefault();
    el.scrollTop += e.deltaY;
  };

  const handleSubmitAssignment = async () => {
    const cleanTitle = title.trim();
    if (!tenant || !courseId) {
      toast.error('Missing course context for assignment creation.');
      return;
    }
    const resolvedCourseStructureId =
      assignmentBaseMarkingScheme?.course_structure?.id || courseStructureId;
    if (!resolvedCourseStructureId) {
      toast.error('Assignment marking structure is not configured for this course.');
      return;
    }
    if (!cleanTitle) {
      toast.error('Assignment title is required.');
      return;
    }
    if (!dueDate) {
      toast.error('Due date is required.');
      return;
    }

    const parsedWeightage = Number(weightage);
    if (
      !Number.isFinite(parsedWeightage) ||
      parsedWeightage < 0 ||
      parsedWeightage > remainingAssignmentWeightage
    ) {
      toast.error(`Weightage must be between 0 and ${remainingAssignmentWeightage.toFixed(2)}.`);
      return;
    }

    const parsedFullMarks = Number(fullMarks);
    if (!Number.isFinite(parsedFullMarks) || parsedFullMarks < 0) {
      toast.error('Full marks must be a valid non-negative number.');
      return;
    }

    const assignmentBrief =
      serializedDetailsGetterRef.current?.() ||
      details ||
      '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

    const assignmentTypeValue =
      assignmentType === 'Group assignment' ? 'GROUP' : 'INDIVIDUAL';

    try {
      const subStructureId =
        assignmentToEdit?.marking_scheme?.course_sub_structure ??
        (
          await createCourseSubStructure({
            tenant,
            data: {
              sub_structure_type: cleanTitle,
              description: '-',
              structure: resolvedCourseStructureId,
            },
          }).unwrap()
        ).id;

      const formData = new FormData();
      formData.append(
        'create_marking_scheme',
        JSON.stringify({
          full_marks: String(fullMarks),
          course_percentage: parsedWeightage,
          course: String(courseId),
          course_structure: resolvedCourseStructureId,
          marking_structure_name: cleanTitle,
          course_sub_structure: subStructureId,
        })
      );
      formData.append('assignment_name', cleanTitle);
      formData.append('assignment_brief', assignmentBrief);
      formData.append('submission_deadline', `${dueDate}T21:00:00+05:45`);
      formData.append('assignment_type', assignmentTypeValue);
      formData.append(
        'assignments_skills',
        JSON.stringify(
          selectedSkills.map((skill) => ({
            skills: skill.id,
            required_level: skill.level,
          }))
        )
      );
      if (courseSectionId) {
        formData.append('course_section', String(courseSectionId));
      } else {
        formData.append('course_section', '');
      }
      formData.append('course', String(courseId));
      attachments.forEach((file) => {
        formData.append('assignment_repo', file);
      });

      if (assignmentToEdit?.id) {
        await patchAssignment({
          tenant,
          assignmentId: assignmentToEdit.id,
          data: formData,
        }).unwrap();
        toast.success('Assignment updated successfully.');
      } else {
        await createAssignment({ tenant, data: formData }).unwrap();
        toast.success('Assignment created successfully.');
      }
      onOpenChange(false);
      resetForm();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create assignment.');
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) resetForm();
      }}
    >
      <SheetContent
        side="right"
        className="w-full p-0 sm:max-w-[min(97vw,1500px)]"
        showClose={false}
      >
        <SheetHeader className="border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
            <SheetTitle>{assignmentToEdit ? 'Edit assignment' : 'Add assignment'}</SheetTitle>
          </div>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_430px]">
            <div className="min-h-0 overflow-y-auto border-b p-4 lg:border-r lg:border-b-0">
              <div className="flex min-h-0 flex-col gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Title</label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium">Assignment details</label>
                  <div className="relative z-0 h-[500px] overflow-hidden rounded-xl border bg-background/60 p-1">
                    <RichTextEditor
                      value={details}
                      onChange={setDetails}
                      onProvideGetSerializedState={(getSerializedState) => {
                        serializedDetailsGetterRef.current = getSerializedState;
                      }}
                      placeholder="Assignment details"
                      className="[&_.editor-container]:m-0 [&_.editor-container]:h-full [&_.editor-inner]:h-full [&_.editor-scroller]:max-h-full [&_.editor-scroller]:overflow-y-auto [&_.editor-input]:min-h-full"
                      minHeight={0}
                    />
                  </div>
                </div>

                <div className="relative z-10 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Skills</p>
                    {selectedSkills.length > 0 ? (
                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 text-white hover:bg-slate-800"
                        onClick={() => {
                          if (selectedFrameworkKeys.length) {
                            openAddSkillsDrawer(selectedFrameworkKeys);
                          } else {
                            setIsFrameworkDrawerOpen(true);
                          }
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>

                  {selectedSkills.length > 0 ? (
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {selectedSkills.map((skill) => (
                        <div
                          key={skill.key}
                          className="rounded-lg border border-blue-400/60 bg-blue-100/60 px-3 py-2 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-100"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm leading-snug">{skill.name}</p>
                            <p className="text-sm font-medium">{skill.level.toFixed(1)}</p>
                          </div>
                          <div className="mt-2 flex items-center justify-end gap-3 text-blue-500 dark:text-blue-300">
                            <Star className="h-4 w-4 fill-current" />
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedSkills((prev) => prev.filter((item) => item.key !== skill.key))
                              }
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mx-auto mt-1 w-full max-w-[560px] rounded-xl border border-blue-200 bg-blue-100/60 px-4 py-3 text-slate-700 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-100/90">
                      <p className="text-sm">Do you want to select skills framework before adding skills?</p>
                      <p className="mt-2 font-medium">How it works</p>
                      <ol className="mt-1 list-decimal space-y-1 pl-5 text-sm">
                        <li>Select one or more frameworks from your organization or public repository.</li>
                        <li>After adding framework(s), you will be redirected to search and add skills.</li>
                      </ol>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          className="bg-slate-700 text-white hover:bg-slate-800"
                          onClick={() => openAddSkillsDrawer(allFrameworks.map((framework) => framework.key))}
                        >
                          Continue without adding framework
                        </Button>
                        <Button type="button" onClick={() => setIsFrameworkDrawerOpen(true)}>
                          Select framework
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="min-h-0 p-4">
              <div className="flex h-full min-h-0 flex-col gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Due date</label>
                  <StandaloneDatePicker
                    value={selectedDueDate}
                    onChange={(date) => {
                      if (!date) {
                        setDueDate('');
                        return;
                      }
                      setDueDate(format(date, 'yyyy-MM-dd'));
                    }}
                    disabledDates={{ before: today }}
                    placeholder="Pick a due date"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium">Assignment type</label>
                  <Select value={assignmentType} onValueChange={setAssignmentType}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Individual assignment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Individual assignment">Individual assignment</SelectItem>
                      <SelectItem value="Group assignment">Group assignment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Full marks</label>
                    <Input
                      value={fullMarks}
                      onChange={(e) => setFullMarks(e.target.value)}
                      type="number"
                      min={0}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Weightage %</label>
                    <Input
                      value={weightage}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        if (nextValue === '') {
                          setWeightage('');
                          return;
                        }
                        const parsedValue = Number(nextValue);
                        if (!Number.isFinite(parsedValue)) return;
                        setWeightage(
                          String(Math.min(remainingAssignmentWeightage, Math.max(0, parsedValue)))
                        );
                      }}
                      type="number"
                      min={0}
                      max={remainingAssignmentWeightage}
                      step="0.01"
                      placeholder="0"
                    />
                    <p className="text-xs text-muted-foreground">
                      Remaining allowed weightage: {remainingAssignmentWeightage.toFixed(2)}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Add attachments</label>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const files = Array.from(e.dataTransfer.files || []);
                      if (files.length) {
                        setAttachments((prev) => [...prev, ...files]);
                      }
                    }}
                    className="w-full rounded-xl border-2 border-dashed border-muted-foreground/30 bg-background/70 px-4 py-8 text-center text-muted-foreground hover:border-primary/40"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-5 w-5" />
                      <p className="font-medium">Drag and drop files here</p>
                      <p className="text-sm">Supported files are PDF, PPT, DOC, DOCX, XLSX, PNG and JPEG.</p>
                      {attachments.length > 0 ? (
                        <p className="text-sm text-foreground">
                          Selected: {attachments.length} file{attachments.length > 1 ? 's' : ''}
                        </p>
                      ) : null}
                    </div>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    multiple
                    accept=".pdf,.ppt,.pptx,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length) {
                        setAttachments((prev) => [...prev, ...files]);
                      }
                    }}
                  />
                  {attachments.length > 0 ? (
                    <div className="rounded-md border bg-background p-2">
                      <div className="space-y-1">
                        {attachments.map((file, index) => (
                          <div
                            key={`${file.name}-${file.size}-${index}`}
                            className="flex items-center justify-between gap-2 text-xs"
                          >
                            <span className="truncate">{file.name}</span>
                            <button
                              type="button"
                              className="text-red-500 hover:underline"
                              onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== index))}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {existingAttachments.length > 0 ? (
                    <div className="rounded-md border bg-muted/20 p-2">
                      <p className="mb-1 text-xs font-medium text-muted-foreground">
                        Existing attachments
                      </p>
                      <div className="space-y-1">
                        {existingAttachments.map((file) => (
                          <a
                            key={file.id}
                            href={file.document || '#'}
                            target="_blank"
                            rel="noreferrer"
                            className="block truncate text-xs text-primary hover:underline"
                          >
                            {file.name || file.document}
                          </a>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200">
                  <p className="inline-flex items-start gap-2">
                    <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      <span className="font-semibold">Note:</span> Please include the evaluation
                      criteria in the assignment details. It will be used for grading by AI if you
                      have access to the AI module.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <SheetFooter className="border-t px-4 py-3">
            <Button
              type="button"
              className="ml-auto"
              onClick={() => {
                void handleSubmitAssignment();
              }}
              disabled={isCreatingSubStructure || isCreatingAssignment || isPatchingAssignment}
            >
              Save
            </Button>
          </SheetFooter>
        </div>

        <Sheet open={isFrameworkDrawerOpen} onOpenChange={setIsFrameworkDrawerOpen}>
          <SheetContent
            side="right"
            className="w-full p-0 sm:max-w-[min(92vw,980px)]"
            showClose={false}
            hideOverlay
          >
            <SheetHeader className="border-b px-4 py-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
                  onClick={() => setIsFrameworkDrawerOpen(false)}
                >
                  <X className="h-4 w-4" />
                </button>
                <SheetTitle>Select skills framework(s)</SheetTitle>
              </div>
            </SheetHeader>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
              <div className="mb-3 inline-flex items-center gap-3 text-lg">
                <button
                  type="button"
                  className={`border-b pb-1 ${frameworkTab === 'org' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
                  onClick={() => setFrameworkTab('org')}
                >
                  Your organization frameworks
                </button>
                <span className="text-muted-foreground">|</span>
                <button
                  type="button"
                  className={`border-b pb-1 ${frameworkTab === 'public' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
                  onClick={() => setFrameworkTab('public')}
                >
                  Public frameworks
                </button>
              </div>

              <div className="grid auto-rows-min content-start grid-cols-1 gap-3 overflow-y-auto md:grid-cols-2">
                {activeFrameworkList.map((framework) => {
                  const checked = selectedFrameworkKeys.includes(framework.key);

                  return (
                    <label
                      key={framework.key}
                      className="h-fit rounded-lg border bg-card p-3 shadow-sm hover:bg-accent/20"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-base font-semibold leading-tight">{framework.name}</p>
                          <p className="mt-1 text-sm text-muted-foreground">Skill/Competence</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => toggleFramework(framework.key, e.target.checked)}
                          className="mt-1"
                        />
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="mt-3 flex items-center justify-end">
                <Button
                  type="button"
                  onClick={() => openAddSkillsDrawer(selectedFrameworkKeys)}
                  disabled={selectedFrameworkKeys.length === 0}
                >
                  Continue
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        <Sheet open={isAddSkillsDrawerOpen} onOpenChange={setIsAddSkillsDrawerOpen}>
          <SheetContent
            side="right"
            className="w-full p-0 sm:max-w-[min(96vw,1360px)]"
            showClose={false}
            hideOverlay
          >
            <SheetHeader className="border-b px-4 py-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
                  onClick={() => setIsAddSkillsDrawerOpen(false)}
                >
                  <X className="h-4 w-4" />
                </button>
                <SheetTitle>Add Skills</SheetTitle>
              </div>
            </SheetHeader>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={skillSearch}
                  onChange={(e) => setSkillSearch(e.target.value)}
                  placeholder="Search skills"
                  className="h-10 pl-9"
                />
              </div>

              <div className="mt-3 rounded-xl border bg-slate-100/70 p-3 dark:bg-slate-900/30">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-muted-foreground">
                    Searching skills from these frameworks:
                  </p>
                  <button
                    type="button"
                    className="text-sm text-primary hover:underline"
                    onClick={() => setIsFrameworkDrawerOpen(true)}
                  >
                    Manage
                  </button>
                </div>
                <ul className="mt-2 list-disc pl-5 text-primary">
                  {frameworkNamesForSearch.map((name, index) => (
                    <li key={`${name}-${index}`}>{name}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-3 grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden lg:grid-cols-2">
                <div className="min-h-0 flex h-full flex-col">
                  <p className="mb-3 text-sm text-muted-foreground">Results: {filteredSkillCandidates.length}</p>
                  <div
                    ref={availableSkillsScrollRef}
                    className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-2"
                    onWheelCapture={(e) => handleSkillsPaneWheel(e, 'available')}
                  >
                    <div className="space-y-5">
                      {filteredSkillCandidates
                        .filter((skill) => !draftSelectedSkills[skill.key])
                        .map((skill) => {
                          const level = draftSkillLevels[skill.key] ?? 0;
                          return (
                            <div key={skill.key} className="rounded-lg border bg-card p-3 space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-base font-semibold">{skill.name}</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">
                                    {getSkillLevelLabel(level)}
                                  </span>
                                  <button
                                    type="button"
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200"
                                    onClick={() => {
                                      setDraftSelectedSkills((prev) => ({
                                        ...prev,
                                        [skill.key]: { ...skill, level },
                                      }));
                                    }}
                                  >
                                    <Plus className="h-5 w-5" />
                                  </button>
                                </div>
                              </div>
                              <Slider
                                min={0}
                                max={5}
                                step={1}
                                value={[level]}
                                onValueChange={(values) => {
                                  const nextLevel = values[0] ?? 0;
                                  setDraftSkillLevels((prev) => ({ ...prev, [skill.key]: nextLevel }));
                                  if (draftSelectedSkills[skill.key]) {
                                    setDraftSelectedSkills((prev) => ({
                                      ...prev,
                                      [skill.key]: { ...prev[skill.key], level: nextLevel },
                                    }));
                                  }
                                }}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>

                <div className="min-h-0 flex h-full flex-col border-l pl-4">
                  <p className="mb-3 text-sm text-muted-foreground">
                    Selected skills: {Object.keys(draftSelectedSkills).length}
                  </p>
                  <div
                    ref={selectedSkillsScrollRef}
                    className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-2"
                    onWheelCapture={(e) => handleSkillsPaneWheel(e, 'selected')}
                  >
                    <div className="space-y-5">
                      {Object.values(draftSelectedSkills).map((skill) => (
                        <div key={skill.key} className="rounded-lg border bg-card p-3 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-base font-semibold">{skill.name}</p>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">
                                {getSkillLevelLabel(skill.level)}
                              </span>
                              <button
                                type="button"
                                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                                onClick={() =>
                                  setDraftSelectedSkills((prev) => {
                                    const next = { ...prev };
                                    delete next[skill.key];
                                    return next;
                                  })
                                }
                              >
                                <X className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                            <div
                              className="h-full rounded-full bg-blue-400 transition-all"
                              style={{ width: `${(skill.level / 5) * 100}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-end">
                <Button
                  type="button"
                  onClick={() => {
                    setSelectedSkills(Object.values(draftSelectedSkills));
                    setIsAddSkillsDrawerOpen(false);
                  }}
                >
                  <Check className="h-4 w-4" />
                  Add skills
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </SheetContent>
    </Sheet>
  );
};

export default CourseAssignmentCreateDrawer;
